import { useEffect, useRef, useState } from 'react'
import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { CITY } from '@city'
import { WebGL2Renderer } from '../../renderer/WebGL2Renderer'
import { buildViewMatrix } from '../../renderer/viewMatrix'
import { PrecomputedMmtgProvider } from '../../geometry/PrecomputedMmtgProvider'
import { clearColorFor } from '../MetroMapCanvas.helpers'
import { StationLabelsOverlay, type StationLabelsOverlayHandle } from '../StationLabelsOverlay'
import { miniMapCamera } from './miniMapCamera'
import { StationMarkerBubble } from './StationMarkerBubble'
import { useStationPageTheme } from './useStationPageTheme'
import styles from './StationMapPreview.module.css'

interface Props {
  /** Station centre in schema/canvas units. */
  stationX: number
  stationY: number
  /** Deep link target: the main map with this station as From. */
  href: string
}

/**
 * Read-only live map preview of the Overview section — Figma
 * 8597:41927. The REAL schema renderer (WebGL2 + MMTG + label raster)
 * drawn once from a static camera centred on the station; the "A"
 * bubble marks it as the route origin the click seeds. The whole
 * widget is one link to the main map (`/<locale>?from=<slug>`), with
 * the "Interactive map" pill as the visible affordance.
 *
 * Boot is fail-safe (Safe-client rule 2): where WebGL2 or the
 * geometry fetch is unavailable the canvas stays blank but the link
 * and the button keep working — the preview is an affordance, not the
 * product.
 */
export function StationMapPreview({ stationX, stationY, href }: Props): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const labelsRef = useRef<StationLabelsOverlayHandle | null>(null)
  const { t } = useTranslation('station-page')
  const [markerCss, setMarkerCss] = useState<{ x: number; y: number } | null>(null)
  // Same signal as the CSS tokens — the geometry set, clear color and
  // label raster flip together with the page palette.
  const theme = useStationPageTheme()

  /* v8 ignore start — WebGL2 + MMTG boot is jsdom-unreachable; covered by
     the Playwright visual baseline (station-overview.spec.ts). */
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (container === null || canvas === null) return

    let renderer: WebGL2Renderer | null = null
    let disposed = false

    const drawAt = (width: number, height: number): void => {
      const dpr = window.devicePixelRatio || 1
      const cam = miniMapCamera({ stationX, stationY, cssWidth: width, cssHeight: height, dpr })
      canvas.width = Math.round(width * dpr)
      canvas.height = Math.round(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`
      renderer?.draw(
        buildViewMatrix(cam.deviceZoom, cam.offsetX, cam.offsetY, canvas.width, canvas.height),
        clearColorFor(theme),
        { zoom: cam.deviceZoom, offsetX: cam.offsetX, offsetY: cam.offsetY },
      )
      labelsRef.current?.apply(
        cam.deviceZoom,
        cam.offsetX,
        cam.offsetY,
        dpr,
        canvas.width,
        canvas.height,
      )
      setMarkerCss(cam.worldToCss(stationX, stationY))
    }

    const redraw = (): void => {
      const rect = container.getBoundingClientRect()
      if (rect.width > 0 && rect.height > 0) drawAt(rect.width, rect.height)
    }

    const boot = async (): Promise<void> => {
      try {
        renderer = new WebGL2Renderer(canvas, { tileCulling: CITY.mapTileCulling })
        const set = await new PrecomputedMmtgProvider().load(theme)
        if (disposed) return
        renderer.uploadGeometry(set)
        redraw()
      } catch {
        // Blank canvas; the link + button still work.
      }
    }
    void boot()

    // jsdom has no ResizeObserver; the boot above already no-ops there.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(redraw)
    observer?.observe(container)
    return () => {
      disposed = true
      observer?.disconnect()
      renderer?.dispose()
    }
  }, [stationX, stationY, theme])
  /* v8 ignore stop */

  return (
    <Link to={href} className={styles.preview} aria-label={t('map_preview_aria')}>
      <div ref={containerRef} className={styles.viewport}>
        <canvas ref={canvasRef} className={styles.canvas} />
        <StationLabelsOverlay ref={labelsRef} theme={theme} onReady={() => {}} />
        {/* Washed-out map per the design (8597:41927) — same CSS-dim
            approach as the main map's dim layer. Label highlighting for
            the selected station is deliberately absent on web (label
            raster, audit finding H-1); the A-bubble carries the focus. */}
        <div className={styles.dim} aria-hidden="true" />
        {markerCss !== null && <StationMarkerBubble x={markerCss.x} y={markerCss.y} />}
      </div>
      <span className={styles.button}>{t('map_preview_button')}</span>
    </Link>
  )
}
